/** @format */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { CrossIcon, SearchIcon } from 'src/components/icons';
import { useTranslation } from 'react-i18next';

type KeywordSearchInputProps = {
  placeholder?: string;
  initialKeywords?: string[];
  onKeywordsChange?: (keywords: string[]) => void;
};

export const KeywordSearchInput = ({
  placeholder,
  initialKeywords = [],
  onKeywordsChange
}: KeywordSearchInputProps) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.search');
  const [value, setValue] = useState('');
  const [keywords, setKeywords] = useState<string[]>(initialKeywords);
  const prevInitialKeywordsRef = useRef<string[] | undefined>(undefined);

  useEffect(() => {
    const prevSorted = prevInitialKeywordsRef.current ? [...prevInitialKeywordsRef.current].sort().join(',') : '';
    const currentSorted = [...initialKeywords].sort().join(',');

    if (prevSorted !== currentSorted) {
      setKeywords(initialKeywords);
      prevInitialKeywordsRef.current = initialKeywords;
    }
  }, [initialKeywords]);

  const handleAddKeyword = () => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    const normalizedValue = trimmedValue.toLowerCase();
    const isDuplicated = keywords.some(keyword => keyword.toLowerCase() === normalizedValue);
    if (isDuplicated) {
      setValue('');
      return;
    }

    const nextKeywords = [...keywords, trimmedValue];
    setKeywords(nextKeywords);
    setValue('');
    if (onKeywordsChange) onKeywordsChange(nextKeywords);
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    const nextKeywords = keywords.filter(keyword => keyword !== keywordToRemove);
    setKeywords(nextKeywords);
    if (onKeywordsChange) onKeywordsChange(nextKeywords);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' && event.key !== 'NumpadEnter') return;
    event.preventDefault();
    handleAddKeyword();
  };

  return (
    <div className="keyword-search">
      <div className="keyword-search__field">
        <input
          type="search"
          className="search-page__query-input"
          value={value}
          onChange={event => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          aria-label={resolvedPlaceholder}
        />
        <button
          type="button"
          className="search-page__query-submit"
          onClick={handleAddKeyword}
          aria-label={t('searchPage.addKeywordAria')}>
          <SearchIcon />
        </button>
      </div>

      {keywords.length > 0 ? (
        <div className="keyword-search__keywords">
          {keywords.map(keyword => (
            <span key={keyword} className="keyword-search__keyword" aria-label={keyword}>
              <span>{keyword}</span>
              <button
                type="button"
                className="keyword-search__keyword-remove"
                onClick={() => handleRemoveKeyword(keyword)}
                aria-label={t('searchPage.removeKeywordAria', { keyword })}>
                <CrossIcon />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};
